import Check from '@mui/icons-material/Check';
import CheckCircle from '@mui/icons-material/CheckCircle';
import type { ReactNode } from 'react';
import type { PublicPlanItem } from '@practium/features/api';
import { formatIntervalLabel, formatIntervalRateLabel, formatPlanPriceBrl } from '../../planFormatters';
import styles from './PlanCard.module.css';

interface PlanCardProps {
	plan: PublicPlanItem['plan'];
	selected?: boolean;
	onSelect?: () => void;
	variant?: 'grid' | 'compact';
	action?: ReactNode;
	advantagesOverride?: string[];
	precedingPlanName?: string | null;
}

function renderAdvantageText(text: string) {
	const parts = text.split(/(\d+|ilimitad[oa]s?)/gi);
	return parts.map((part, index) =>
		/^\d+$/.test(part) || /^ilimitad[oa]s?$/i.test(part) ? (
			<strong key={index}>{part}</strong>
		) : (
			<span key={index}>{part}</span>
		)
	);
}

export const PlanCard = ({
	plan,
	selected = false,
	onSelect,
	variant = 'grid',
	action,
	advantagesOverride,
	precedingPlanName,
}: PlanCardProps) => {
	const intervalLabel = formatIntervalLabel(plan.interval, plan.intervalCount);
	const priceLabel = formatPlanPriceBrl(plan.price);

	if (variant === 'compact') {
		return (
			<div className={`${styles.compactCard} ${selected ? styles.selected : ''}`}>
				<div className={styles.compactBadge}>
					<Check className={styles.compactBadgeIcon} />
				</div>
				<div className={styles.compactInfo}>
					<span className={styles.compactName}>
						{plan.name} · {intervalLabel}
					</span>
					<span className={styles.compactPrice}>R$ {priceLabel}</span>
				</div>
				{action && <div className={styles.compactAction}>{action}</div>}
			</div>
		);
	}

	const advantages = advantagesOverride ?? plan.advantages;
	const intervalRateLabel = formatIntervalRateLabel(plan.interval, plan.intervalCount);

	return (
		<div className={styles.card}>
			<div className={styles.cardBody}>
				<span className={styles.name}>{plan.name}</span>

				<div className={styles.priceRow}>
					<span className={styles.pricePrefix}>R$</span>
					<span className={styles.price}>{priceLabel}</span>
					<span className={styles.interval}>/{intervalRateLabel}</span>
				</div>

				{plan.maxInstallments > 1 && (
					<span className={styles.installmentNote}>
						Em até {plan.maxInstallments}x sem juros no cartão
					</span>
				)}

				{precedingPlanName && (
					<p className={styles.inheritedNote}>Tudo do plano {precedingPlanName}, mais:</p>
				)}

				{advantages.length > 0 && (
					<ul className={styles.advantagesList}>
						{advantages.map((advantage) => (
							<li key={advantage} className={styles.advantageItem}>
								<CheckCircle className={styles.advantageIcon} />
								<span className={styles.advantageText}>{renderAdvantageText(advantage)}</span>
							</li>
						))}
					</ul>
				)}
			</div>

			{onSelect && (
				<button type="button" className={styles.selectButton} onClick={onSelect}>
					Selecionar {plan.name}
				</button>
			)}
		</div>
	);
};
